import Dropdown from '@jetbrains/ring-ui-built/components/dropdown/dropdown'
import type {LinkProps} from '@jetbrains/ring-ui-built/components/link/link'
import Link from '@jetbrains/ring-ui-built/components/link/link'
import {Directions} from '@jetbrains/ring-ui-built/components/popup/popup.consts'
import type {QueryParams} from '@jetbrains/teamcity-ui/dist/utils'
import classnames from 'classnames'
import type {Ref} from 'react'
import * as React from 'react'
import {useMemo} from 'react'

import EntityLink from '../../../containers/EntityLink'
import {useHash} from '../../../hooks/routes'
import {
  getIsSakuraUI,
  makeBuildBranchSelector,
  makeGetClassicUILinkWithBranch,
} from '../../../selectors'
import type {PipelineId, BuildId, BuildTypeId, ProjectId} from '../../../types'
import {stringifyId} from '../../../types'
import {trackBuildNavigationClick} from '../Builds/BuildDetails/BuildDetails.analyticEvents'
import Popup from '../Popup/Popup.lazy'
import Icon from '../ProjectOrBuildTypeIcon/OverviewStatusIcon.container'
import {EntityIconType} from '../ProjectOrBuildTypeIcon/ProjectOrBuildTypeIcon.types'

import styles from './BuildPath.module.css'

import {useAppSelector} from 'src/hooks/react-redux'

type Props = {
  className?: string
  buildId?: BuildId | null | undefined
  buildTypeId?: BuildTypeId
  projectId?: ProjectId
  pipelineId?: PipelineId | null | undefined
  runId?: string | null | undefined
  jobId?: string | null | undefined
  name: string
  itemKey: string
  setRef?: (el: HTMLElement | null, id: string) => void
  showTitle?: boolean
  showInvestigationInStatus?: boolean
  withIcon?: boolean
  withStatusIcon?: boolean | null | undefined
  secondary?: boolean
  queryParams?: QueryParams
}

function BuildPathLink({
  buildId,
  buildTypeId,
  projectId,
  pipelineId,
  runId,
  name,
  setRef,
  showTitle,
  showInvestigationInStatus,
  className,
  withIcon,
  withStatusIcon,
  itemKey,
  secondary,
  queryParams,
}: Props) {
  const hash = useHash()
  const isExpanded = buildId != null && hash === stringifyId(buildId)
  const clickBuildPathLinkHandler = React.useCallback(() => {
    trackBuildNavigationClick({buildId, targetType: 'other', isExpanded})
  }, [buildId, isExpanded])

  const buildBranchSelector = React.useMemo(() => makeBuildBranchSelector(), [])
  const branch = useAppSelector(state => buildBranchSelector(state, buildId))

  const isSakuraUI = useAppSelector(getIsSakuraUI)

  const getClassicUIHref = React.useMemo(
    () =>
      makeGetClassicUILinkWithBranch(
        {
          buildTypeId,
          projectId,
        },
        branch,
      ),
    [branch, buildTypeId, projectId],
  )
  const classicUIHref = useAppSelector(getClassicUIHref)
  const ref: Ref<HTMLSpanElement> | undefined = React.useMemo(
    () => (setRef != null ? (el: HTMLElement | null) => setRef(el, itemKey) : undefined),
    [itemKey, setRef],
  )
  const classes = classnames(styles.link, className, {
    [styles.secondary]: secondary,
  })
  const iconProps = useMemo(() => {
    if (projectId != null) {
      return {
        type: EntityIconType.PROJECT,
        id: projectId,
      } as const
    }
    if (buildTypeId != null) {
      return {
        type: EntityIconType.BUILD_TYPE,
        id: buildTypeId,
      } as const
    }
    if (pipelineId != null) {
      return {
        type: EntityIconType.PIPELINE,
        id: pipelineId,
      } as const
    }
    return {
      type: undefined,
    }
  }, [buildTypeId, pipelineId, projectId])
  const commonProps: Pick<LinkProps, 'className' | 'data-test' | 'children' | 'onClick'> = {
    className: classes,
    'data-test': 'path-link',
    children: name,
    onClick: clickBuildPathLinkHandler,
  }
  const link = isSakuraUI ? (
    <EntityLink
      {...commonProps}
      buildTypeId={buildTypeId}
      projectId={projectId}
      pipelineId={pipelineId}
      runId={runId}
      branch={branch}
      queryParams={queryParams}
    />
  ) : (
    <Link {...commonProps} href={classicUIHref} />
  )

  const icon = useMemo(
    () => (
      <Icon
        {...iconProps}
        className={styles.icon}
        showInvestigationInStatus={showInvestigationInStatus}
        ignoreStatus={withStatusIcon !== true}
      />
    ),
    [iconProps, withStatusIcon, showInvestigationInStatus],
  )
  return (
    <span ref={ref} className={styles.item}>
      {(withIcon === true || withStatusIcon === true) && icon}
      {showTitle === true ? (
        <Dropdown
          className={styles.pathItemDropDown}
          anchor={link}
          hoverShowTimeOut={300}
          hoverHideTimeOut={300}
          clickMode={false}
          hoverMode
        >
          <Popup left={-1} directions={[Directions.TOP_RIGHT, Directions.BOTTOM_RIGHT]}>
            <div className={styles.hiddenPathPopup}>{link}</div>
          </Popup>
        </Dropdown>
      ) : (
        link
      )}
    </span>
  )
}

export default React.memo(BuildPathLink)
